import {
  LitComponent,
  TemplateResult,
  render,
  html,
} from 'derivita/util/lit_component/lit_component';
import { toast } from 'derivita/util/toast/toast';
// taze: componentHandler from //ui/mdl:mdl_dts
// taze: Html5Qrcode from //third_party/html5-qrcode:definitions

const HEADER_HEIGHT = 56;
const DESCRIPTION_HEIGHT = 72;

class ScannerApp extends LitComponent {
  private reader_?: Html5Qrcode;
  private reading_ = false;
  private loadingFailed_ = false;

  protected cssClassNames(): string | string[] {
    return 'container';
  }
  protected renderTemplate(): TemplateResult {
    if (this.loadingFailed_) {
      return html` ${this.loadingFailureTemplate()} `;
    }

    return html`
      <div class="description">
        Use the QR Scanner below to scan <br />
        your homework submission QR code.
      </div>
      <div class="reader-container">
        <div id="reader"></div>
      </div>
    `;
  }

  private loadingFailureTemplate() {
    return html`
      <div class="info-page">
        <br />
        <i class="material-icons">error</i>
        <div>
          QR Scanner failed to load. Please use your camera app to scan the
          code. code.
        </div>
      </div>
    `;
  }

  updated() {
    componentHandler.upgradeElements(this.getRootElement());

    if (this.reader_) {
      this.reader_.stop().then(() => {
        this.createCamera();
      });
    } else {
      this.createCamera();
    }
  }

  async createCamera(): Promise<void> {
    try {
      let aspectRatio: number;
      let cameraId: string;

      try {
        ({ aspectRatio, cameraId } = await getCameraFromConstraints({
          video: {
            facingMode: 'environment',
          },
          audio: false,
        }));
      } catch (e) {
        ({ aspectRatio, cameraId } = await getCameraFromConstraints({
          video: true,
          audio: false,
        }));
      }

      const { clientWidth, clientHeight } = document.documentElement;
      const vw = Math.max(clientWidth || 0, window.innerWidth || 0);
      const vh = Math.max(clientHeight || 0, window.innerHeight || 0);
      const scaledWidth =
        (vh - HEADER_HEIGHT - DESCRIPTION_HEIGHT) * aspectRatio;

      this.reader_ = new Html5Qrcode('reader');
      const readerElement = this.getRootElement().querySelector(
        '#reader'
      )! as HTMLDivElement;

      // Insure video element is always scaled down to fit inside window.
      const readerWidth = Math.min(scaledWidth, vw);
      readerElement.style.width = `${readerWidth}px`;

      const config = { fps: 5, qrbox: 250 };
      await this.reader_!.start(cameraId, config, (msg: string) => {
        this.onDecodeSuccess(msg);
      });
    } catch (e) {
      this.loadingFailed_ = true;
      const _ = this.requestUpdate();
    }
  }

  /**
   *  Called when qr code has been successfully scanned.
   */
  private onDecodeSuccess(msg: string) {
    if (this.reading_) {
      return;
    }

    this.reading_ = true;

    const isValidHostname = msg.includes(window.location.hostname);
    const isSecure = isValidHttpsUrl(msg);

    if (isValidHostname && isSecure) {
      window.location.href = msg;
      return;
    }

    if (!isSecure) {
      toast(`Non-secure url from QR code: ${msg}`);
    } else if (!isValidHostname) {
      toast(`Invalid hostname from QR code: ${msg}`);
    } else {
      toast(`Invalid QR code: ${msg}`);
    }

    this.reading_ = false;
  }
}

function isValidHttpsUrl(raw: string) {
  let url: URL;

  try {
    url = new URL(raw);
  } catch (_) {
    return false;
  }

  return url.protocol === 'https:';
}

async function getCameraFromConstraints(constraints: MediaStreamConstraints) {
  const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

  const track = mediaStream.getVideoTracks()[0];

  if (track == undefined) {
    throw new Error('Expected environment facing camera.');
  }

  const cameraId = track.getCapabilities().deviceId;

  if (typeof cameraId !== 'string') {
    throw new Error('Expected camera id from media track.');
  }

  return { aspectRatio: track.getSettings().aspectRatio ?? 1, cameraId };
}

render(new ScannerApp(), document.getElementById('container')!);

// Reload the scanner if navigated back to through browser history nav.
window.addEventListener('pageshow', (event) => {
  try {
    const historyTraversal =
      event.persisted ||
      (typeof window.performance !== 'undefined' &&
        (window.performance.getEntriesByType('navigation')[0] as any).type ===
          'back_forward');
    if (historyTraversal) {
      render(new ScannerApp(), document.getElementById('container')!);
    }
  } catch (e) {}
});
